import React, { useState } from "react";
import { HiX } from "react-icons/hi";
import { usePostRoboMarketsApiKeyMutation } from "@repo/api";
import { useToast, useTranslation } from "@repo/core";

interface AddRoboMarketsKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddRoboMarketsKeyModal: React.FC<AddRoboMarketsKeyModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [apiKey, setApiKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addSuccessMessage, addErrorMessage } = useToast();
  const { mutate: postApiKey } = usePostRoboMarketsApiKeyMutation();
  const { t } = useTranslation();

  const handleSave = async () => {
    if (!apiKey.trim()) {
      addErrorMessage(t("robomarketsApiKey.validationError"));
      return;
    }

    setIsSubmitting(true);
    try {
      await postApiKey({ apiKey: apiKey.trim() });
      addSuccessMessage(t("robomarketsApiKey.successMessage"));
      setApiKey("");
      onClose();
    } catch (error) {
      console.error("Error saving API key:", error);
      addErrorMessage(t("robomarketsApiKey.errorMessage"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setApiKey("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t("robomarketsApiKey.modalTitle")}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isSubmitting}
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="mb-6">
          <label
            htmlFor="apiKey"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t("robomarketsApiKey.apiKeyLabel")}
          </label>
          <input
            type="text"
            id="apiKey"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={t("robomarketsApiKey.apiKeyPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            disabled={isSubmitting}
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            {t("robomarketsApiKey.close")}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting || !apiKey.trim()}
          >
            {isSubmitting ? t("robomarketsApiKey.saving") : t("robomarketsApiKey.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRoboMarketsKeyModal;
